if (!customElements.get("ff-buy-buttons")) {
  customElements.define(
    "ff-buy-buttons",
    class FFBuyButtons extends HTMLElement {
      constructor() {
        super();
        
        const { hasSubscription, hasOnetime } = this.dataset;
        const isSingle = hasSubscription !== hasOnetime;
        this.allVariantsSelected = false;
        this.currentVariant = null;
        this.variantAvailable = this.dataset.variantAvailable === 'true';
        this.outOfStockText = this.dataset.outOfStockText;
        this.subscribeText = this.dataset.subscribeText;
        this.addToCardText = this.dataset.addToCardText;
        
        // Delegation: radio changes
        this.addEventListener('change', e => {
          const { classList } = e.target;
          if (classList.contains('ff-subscription-plan__radio')) {
            this.handlePlanSelection(e.target);
          } else if (classList.contains('ff-buy-button__radio') && !isSingle) {
            this.updateOption(e.target);
          }
        });
        
        // Delegation: label clicks (toggle collapse)
        if (!isSingle) {
          this.addEventListener('click', e => {
            const label = e.target.closest('.ff-buy-button__label');
            if (label && !e.target.closest('input[type="radio"]')) {
              this.toggleCollapse(label, e);
            }
          });
        }
        
        // Gestionar selector de cantidad compartido
        this.setupSharedQuantitySelector();
        
        // Escuchar eventos del variant selector
        this.setupVariantListeners();
        
        // Inicialización
        if (hasSubscription === 'true') this.initState();
        requestAnimationFrame(() => {
          this.checkVariantSelector();
          this.updateQuantitySelector();
          this.updateAllButtons();
        });
      }
      
      setupSharedQuantitySelector() {
        const quantityInput = this.querySelector('.ff-buy-button__quantity-input');
        const decBtn = this.querySelector('.ff-buy-button__quantity-btn[data-action="decrement"]');
        const incBtn = this.querySelector('.ff-buy-button__quantity-btn[data-action="increment"]');
        const sharedHiddenInputs = this.querySelectorAll('.ff-shared-quantity-hidden');
        
        if (!quantityInput || !decBtn || !incBtn) return;
        
        const STEP = Number(quantityInput.step || 1);
        const MIN = Number(quantityInput.min || 1);
        const clamp = (v) => Math.max(MIN, v);
        
        const setValue = (val) => {
          const newVal = clamp(Number(val));
          quantityInput.value = newVal;
          sharedHiddenInputs.forEach(input => {
            input.value = newVal;
          });
        };
        
        decBtn.addEventListener("click", () => {
          setValue(Number(quantityInput.value) - STEP);
        });
        
        incBtn.addEventListener("click", () => {
          setValue(Number(quantityInput.value) + STEP);
        });
        
        quantityInput.addEventListener("input", () => {
          setValue(quantityInput.value);
        });
        
        // Inicializar valores
        sharedHiddenInputs.forEach(input => {
          input.value = quantityInput.value;
        });
      }
      
      setupVariantListeners() {
        const getProductId = () => this.querySelector('ff-product-form')?.dataset.sectionId;
        
        document.addEventListener('variant-selector:validation-change', (e) => {
          if (getProductId() === e.detail.productId) {
            this.allVariantsSelected = e.detail.allVariantsSelected;
            this.updateAllButtons();
          }
        });
        
        document.addEventListener('variant-selector:change', (e) => {
          if (getProductId() === e.detail.productId) {
            this.currentVariant = e.detail.variant;
            this.updateAllButtons();
          }
        });
      }
      
      checkVariantSelector() {
        const formWrapper = this.querySelector('ff-product-form');
        if (!formWrapper) {
          this.allVariantsSelected = true;
          return;
        }
        
        const sectionId = formWrapper.dataset.sectionId;
        const variantSelector = document.querySelector(`.ff-variant-selector[data-product-id="${sectionId}"]`);
        
        if (!variantSelector) {
          // Producto sin selector de variantes (una sola variante)
          this.allVariantsSelected = true;
          return;
        }
        
        const options = variantSelector.querySelectorAll('.ff-variant-option');
        if (options.length === 0) {
          this.allVariantsSelected = true;
          return;
        }
        
        // Verificar si todas las opciones tienen selección
        this.allVariantsSelected = Array.from(options).every(opt => 
          opt.classList.contains('ff-variant-option--has-selection')
        );
      }

      toggleCollapse(target, e) {
        const option = target.closest('.ff-buy-button__option--subscription');
        if (option?.classList.contains('ff-buy-button__option--active')) {
          e.preventDefault();
          e.stopPropagation();
          option.classList.toggle('ff-buy-button__option--collapsed');
        }
      }

      handlePlanSelection(radio) {
        this.updatePlan(radio);
      }

      updateOption(radio) {
        const parent = radio.closest('.ff-buy-button__option');
        const isSub = radio.value === 'subscription';
        
        this.querySelectorAll('.ff-buy-button__option').forEach(opt => {
          const isActive = opt === parent;
          opt.classList.toggle('ff-buy-button__option--active', isActive);
          if (isActive && opt.classList.contains('ff-buy-button__option--subscription')) {
            opt.classList.remove('ff-buy-button__option--collapsed');
          }
        });
        
        this.querySelector('.ff-buy-button__form--subscription')?.classList.toggle('ff-d-none', !isSub);
        this.querySelector('.ff-buy-button__form--onetime')?.classList.toggle('ff-d-none', isSub);
        
        // Mostrar/ocultar selector de cantidad compartido
        this.updateQuantitySelector();
        this.updateAllButtons();
      }

      updatePlan(radio) {
        const plan = radio.closest('.ff-subscription-plan');
        const { planName = '', planSavings = '0', savingsText = '' } = plan.dataset;
        
        this.querySelectorAll('.ff-subscription-plan').forEach(p => 
          p.classList.toggle('ff-subscription-plan--active', p === plan)
        );
        
        const input = this.querySelector('[id*="ff-selling-plan-input"]');
        if (input) input.value = radio.value;
        
        const freq = this.querySelector('[data-subscription-frequency]');
        if (freq) {
          freq.textContent = planName;
          freq.classList.toggle('ff-d-none', !planName);
        }
        
        const badge = this.querySelector('[data-savings-badge]');
        if (badge) {
          const hasSavings = parseFloat(planSavings) > 0;
          badge.textContent = hasSavings ? savingsText : '';
          badge.classList.toggle('ff-d-none', !hasSavings);
        }
        
        this.updateQuantitySelector();
        this.updateAllButtons();
      }

      initState() {
        const checked = this.querySelector('.ff-subscription-plan__radio:checked');
        checked ? this.updatePlan(checked) : this.setDefaults();
      }
      
      setDefaults() {
        const option = this.querySelector('.ff-buy-button__option--subscription');
        if (!option) return;
        
        const freq = this.querySelector('[data-subscription-frequency]');
        if (freq) freq.textContent = option.dataset.chooseFrequency || '';
        
        const badge = this.querySelector('[data-savings-badge]');
        if (badge) badge.textContent = option.dataset.savingsBadgeDefault || '';
        
        this.updateAllButtons();
      }
      
      updateQuantitySelector() {
        const sharedQuantity = this.querySelector('.ff-buy-button__quantity-selector.ff-shared-quantity');
        if (!sharedQuantity) return;
        
        // Mostrar selector de cantidad para la opción activa
        const activeOption = this.querySelector('.ff-buy-button__option--active');
        if (activeOption) {
          sharedQuantity.classList.add('ff-show');
        } else {
          sharedQuantity.classList.remove('ff-show');
        }
      }
      
      updateAllButtons() {
        if (this.currentVariant) this.variantAvailable = this.currentVariant.available;
        
        // Botón de suscripción: requiere variantes + plan + disponibilidad
        const subscriptionBtn = this.querySelector('.ff-buy-button__form--subscription [type="submit"]');
        if (subscriptionBtn) {
          const hasPlanSelected = !!this.querySelector('.ff-subscription-plan__radio:checked');
          const canEnable = this.allVariantsSelected && hasPlanSelected && this.variantAvailable;
          
          if (!this.variantAvailable) subscriptionBtn.children[0].textContent = this.outOfStockText;
          else subscriptionBtn.children[0].textContent = this.subscribeText;

          subscriptionBtn.disabled = !canEnable;
          subscriptionBtn.classList.toggle('ff-button-container--blocked', !canEnable);
          subscriptionBtn.classList.toggle('ff-button-container--unavailable', !this.variantAvailable);
        }
        
        // Botón one-time: requiere variantes + disponibilidad
        const onetimeBtn = this.querySelector('.ff-buy-button__form--onetime [type="submit"]');
        if (onetimeBtn) {
          const canEnable = this.allVariantsSelected && this.variantAvailable;

          if (!this.variantAvailable) onetimeBtn.children[0].textContent = this.outOfStockText;
          else onetimeBtn.children[0].textContent = this.addToCardText;
          
          onetimeBtn.disabled = !canEnable;
          onetimeBtn.classList.toggle('ff-button-container--blocked', !canEnable);
          onetimeBtn.classList.toggle('ff-button-container--unavailable', !this.variantAvailable);
        }
      }
      
      toggleSubscriptionButton() {
        this.updateAllButtons();
      }
    }
  );
}

